import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TablePagination,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "react-query";

import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getSessions } from "../../../api/services/api";
import EmptyScreen from "../../../components/EmptyScreen";
import { ViewButton } from "../../../components/IconButtons";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";
import textCapitalization from "../../../components/textCapitalization";
import {
  ModernHeader,
  StatusChip,
  getStatusVariant,
} from "../../../components/modern";
import {
  formatMoney,
  getSessionPayment,
} from "../../../utils/sessionFields";

interface ModeOption {
  mode: string;
}

export const SessionsList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [modeValue, setModeValue] = useState<ModeOption[]>([]);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [status, setStatus] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data, isLoading } = useQuery(
    [
      "sessions",
      {
        status: [status],
        search: search,
        modeOfConsultation: modeValue
          .map((mode) => mode.mode.toUpperCase())
          .join(","),
        fromDate: startDate,
        toDate: endDate,
        limit: limit,
        offset: limit * page,
      },
    ],
    getSessions
  );

  // Debug: Log first session data structure
  // console.log(JSON.stringify(data?.data?.sessions?.slice(0,1)))

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilter = () => {
    setModeValue([]);
    setStatus(null);
    setStartDate(null);
    setEndDate(null);
  };

  const formatName = (name: any) => {
    if (!name) return "";
    return name
      .split(" ")
      .map(
        (word: any) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  };

  const columns = [
    {
      title: "Session ID",
      dataIndex: "sessionId",
      key: "sessionId",
    },
    {
      title: "Client Name",
      dataIndex: "patientProfiles.fullName",
      key: "patientProfiles.fullName",
      render: (row: any, colData: any) => {
        return formatName(colData?.patientProfiles?.fullName || "N/A");
      },
    },
    {
      title: "Therapist Name",
      dataIndex: "clinicians.users.fullName",
      key: "clinicians.users.fullName",
      render: (row: any, colData: any) => {
        return formatName(
          colData?.clinicians?.users?.fullName ||
          colData?.clinician?.users?.fullName ||
          "N/A"
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (row: any, colData: any) => {
        // Try multiple date fields from the API response
        const dateValue =
          colData?.sessionTimeslot?.[0]?.date ||
          colData?.date ||
          colData?.session_date;
        if (!dateValue) return "N/A";
        return (
          <div style={{ display: "flex", whiteSpace: "nowrap" }}>
            {moment(dateValue).format("DD MMM YYYY")}
          </div>
        );
      },
    },
    {
      title: "Time slot",
      dataIndex: "time",
      key: "time",
      render: (colData: any, row: any) => (
        <Box display="flex" justifyContent="center" flexDirection="column">
          {row?.sessionTimeslot?.map((item: any, index: number) => (
            <Typography key={index} sx={{ width: "100px", fontSize: "14px" }}>
              {item?.timeSlots?.startAt || item?.timeslot?.startAt} -{" "}
              {item?.timeSlots?.endAt || item?.timeslot?.endAt}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      title: "Mode",
      dataIndex: "consultationMode",
      key: "consultationMode",
      render: (colData: any) => {
        return (
          <Chip
            label={textCapitalization(colData)}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: theme.colors.info.lighter || "#e3f2fd",
              color: theme.colors.info.main || "#1976d2",
            }}
          />
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "sessionPayment",
      key: "amount",
      render: (_colData: any, row: any) => {
        const payment = getSessionPayment(row);
        return (
          <Typography sx={{ fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap" }}>
            {formatMoney(payment?.amount, payment?.currency || "SAR")}
          </Typography>
        );
      },
    },
    {
      title: "Coupon",
      dataIndex: "sessionPayment",
      key: "coupon",
      render: (_colData: any, row: any) => {
        const payment = getSessionPayment(row);
        if (!payment?.couponCode) {
          return (
            <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
              —
            </Typography>
          );
        }
        const discount =
          payment.discountAmount && payment.discountAmount > 0
            ? ` (−${formatMoney(payment.discountAmount, payment.currency || "SAR")})`
            : "";
        return (
          <Chip
            label={`${payment.couponCode}${discount}`}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: theme.colors.warning?.lighter || "#fff8e1",
              color: theme.colors.warning?.main || "#f57c00",
              maxWidth: 220,
            }}
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (colData: any) => {
        return (
          <StatusChip
            status={colData?.replace(/_/g, " ") || "UNKNOWN"}
            variant={getStatusVariant(colData)}
          />
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      render: (colData: any, row: any) => (
        <ViewButton
          onClick={() => {
            return navigate(`/sessions/${row.id}`);
          }}
        />
      ),
    },
  ];

  const modesMenu: ModeOption[] = [
    {
      mode: "Video",
    },
    {
      mode: "Audio",
    },
    {
      mode: "Home",
    },
  ];
  const statusMenu = ["PENDING", "PAID", "STARTED", "COMPLETED", "CANCELLED", "EXPIRED", "REJECTED", "REPORT_SUBMITTED"];

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", pb: 4 }}>
      <ModernHeader
        title="Sessions Management"
        subtitle={`Total: ${data?.data?.count || 0} sessions`}
      />

      <Box px={3}>
        {/* Search and Filter Section */}
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          sx={{
            backgroundColor: "#fff",
            p: 2.5,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            mb: 3,
          }}
        >
          <TextField
            label="Search by Session ID"
            sx={{
              minWidth: "300px",
              flex: 1,
              maxWidth: "400px",
            }}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon />
                </IconButton>
              ),
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={handleClick}
            sx={{
              borderWidth: 2,
              "&:hover": { borderWidth: 2 },
            }}
          >
            Filter
          </Button>
          {(modeValue.length > 0 || status || startDate || endDate) && (
            <Button
              onClick={handleClearFilter}
              variant="text"
              startIcon={<ClearIcon />}
              sx={{
                fontWeight: 600,
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Filter Menu */}
        <Menu
          open={open}
          onClose={handleClose}
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
          }}
        >
          <MenuItem>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                inputFormat="DD/MM/YYYY"
                value={startDate}
                onChange={(newValue: any) => {
                  setStartDate(moment(new Date(newValue)).format("YYYY-MM-DD"));
                }}
                renderInput={(params) => (
                  <TextField sx={{ width: "180px", mr: 1 }} {...params} />
                )}
              />
              <DatePicker
                label="End Date"
                inputFormat="DD/MM/YYYY"
                value={endDate}
                onChange={(newValue: any) => {
                  setEndDate(moment(new Date(newValue)).format("YYYY-MM-DD"));
                }}
                renderInput={(params) => (
                  <TextField sx={{ width: "180px" }} {...params} />
                )}
              />
            </LocalizationProvider>
          </MenuItem>

          <MenuItem>
            <Box sx={{ width: "100%" }}>
              <Autocomplete
                multiple
                options={modesMenu}
                value={modeValue}
                getOptionLabel={(option: any) => option.mode}
                isOptionEqualToValue={(option, value) =>
                  option.mode === value?.mode
                }
                onChange={(event: any, newValues: any) => {
                  setModeValue(newValues);
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    <ListItemText primary={option.mode} />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Mode of Sessions"
                  />
                )}
              />
            </Box>
          </MenuItem>
          <MenuItem>
            <Box sx={{ width: "100%" }}>
              <Autocomplete
                options={statusMenu}
                getOptionLabel={(option) => textCapitalization(option)}
                value={status}
                onChange={(event, newValue: any) => {
                  setStatus(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Status"
                  />
                )}
              />
            </Box>
          </MenuItem>
        </Menu>

        {/* Table Section */}
        {data?.data?.count === 0 ? (
          <EmptyScreen type="Sessions" />
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <Table
                  headAlign={"center"}
                  tableAlign={"center"}
                  key={"sessions"}
                  dataSource={data?.data?.sessions}
                  rowKey={"id"}
                  columns={columns}
                  loading={isLoading}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderTop: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <TablePagination
                    count={data?.data?.count || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
